import React, { useContext, useEffect, useState } from "react";
import { csrfToken } from "../../../../utis/csrfTokenUtils";
import { networkService } from "../../../config/axios";
import { userTypeArr } from "../../../../hooks/utils";
import UserInfoContext from "../../../../utis/userInfoContext";
import { AdminFormInput, AdminModalFooter } from "../../../Admin/Shared";
import "../../../Admin/Shared/AdminFormInput.css";

const AddUserForm = ({setShow, companyId, selectedUser}) => {
    const {setShowNotifications} = useContext(UserInfoContext);

    const initialState = {
        email: "",
        user_type: "",
        profile: {
            first_name: "",
            last_name: "",
            time_zone: "",
        },
    };

    const [user, setUser] = useState(initialState);
    const [timeZoneData, setTimeZoneData] = useState({});
    const [companyDomains, setCompanyDomains] = useState([]);
    const [emailError, setEmailError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isValidEmailFormat = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Fetch company domains
    useEffect(() => {
        const fetchDomains = async () => {
            try {
                const res = await networkService(
                    `admin/company-domains/${companyId}.json`
                );
                setCompanyDomains(res?.data?.map((d) => d.name) || []);
            } catch (e) {
                console.error("Domain fetch failed");
            }
        };
        if (companyId) fetchDomains();
    }, [companyId]);

    const isValidCompanyEmail = (email) => {
        if (!email || !email.includes("@")) return false;
        if (companyDomains.length === 0) return true;
        return companyDomains.includes(email.split("@")[1]);
    };

    const handleChange = ({target: {name, value, type}}) => {
        if (type === "date") value = new Date(value).toISOString();

        if (name === "email") {
            if (!isValidEmailFormat(value)) {
                setEmailError("Please enter a valid email address");
            } else if (
                companyDomains.length > 0 &&
                !isValidCompanyEmail(value)
            ) {
                setEmailError(
                    `Email must belong to company domain (${companyDomains.join(", ")})`
                );
            } else {
                setEmailError("");
            }
        }


        if (["first_name", "last_name", "time_zone"].includes(name)) {
            setUser((prev) => ({
                ...prev,
                profile: {...prev.profile, [name]: value},
            }));
        } else {
            setUser((prev) => ({...prev, [name]: value}));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        if (!isValidEmailFormat(user?.email)) {
            setEmailError("Please enter a valid email address");
            return;
        }
        if (
            companyDomains.length > 0 &&
            !isValidCompanyEmail(user?.email)
        ) {
            setEmailError(
                `Email must belong to company domain (${companyDomains.join(", ")})`
            );
            return;
        }
        setIsSubmitting(true);
        const url = selectedUser?.id
            ? `admin/users/${selectedUser.id}.json`
            : "admin/users.json";

        const payload = {
            authenticity_token: csrfToken(),
            ...(selectedUser?.id && {id: selectedUser.id}),
            user: {
                company_id: companyId,
                email: user.email,
                user_type: user.user_type ?? user.type,
                profile: {
                    first_name: user.profile.first_name,
                    last_name: user.profile.last_name,
                    time_zone: user.profile.time_zone,
                    ...(selectedUser?.id && {id: user.profile?.id}),
                },
            },
        };

        try {
            const res = await networkService(
                url,
                selectedUser?.id ? "PATCH" : "POST",
                payload
            );

            if (res.status === 200 || res.status === 201) {
                setShowNotifications({
                    showToast: true,
                    toastType: "success",
                    toastMessage: `User ${
                        selectedUser?.id ? "Updated" : "Added"
                    } successfully`,
                });

                setShow("list");
                setUser(initialState);
            }
        } catch (err) {
            setShowNotifications({
                showToast: true,
                toastType: "danger",
                toastMessage:
                    err?.response?.data?.errors?.email?.[0] ?? "Something went wrong",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (selectedUser && Object.keys(selectedUser).length) {
            setUser(selectedUser);
            setEmailError("");
        }
    }, [selectedUser]);

    useEffect(() => {
        const fetchTZ = async () => {
            const res = await networkService("time-zones");
            setTimeZoneData(res?.data || {});
        };
        fetchTZ();
    }, []);

    return (
        <form onSubmit={handleSubmit} className="admin-form-page">
            <AdminFormInput
                label="First name"
                name="first_name"
                placeholder="First name"
                value={user.profile?.first_name ?? ""}
                onChange={handleChange}
                required
            />
            <AdminFormInput
                label="Last name"
                name="last_name"
                placeholder="Last name"
                value={user.profile?.last_name ?? ""}
                onChange={handleChange}
            />
            <AdminFormInput
                label="Email"
                name="email"
                placeholder="Email"
                value={user.email ?? ""}
                onChange={handleChange}
                error={emailError}
                required
            />
            <div className="admin-form-row">
                <div className="admin-form-group">
                    <label htmlFor="time_zone" className="admin-form-label">
                        Time zone <span className="text-danger ms-1">*</span>
                    </label>
                    <select
                        id="time_zone"
                        name="time_zone"
                        value={user.profile?.time_zone ?? ""}
                        onChange={handleChange}
                        className="admin-form-input"
                        required
                    >
                        <option value="">Select...</option>
                        {Object.keys(timeZoneData).map((k) => (
                            <option key={timeZoneData[k].id} value={timeZoneData[k].id}>
                                {timeZoneData[k].name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="admin-form-group">
                    <label htmlFor="user_type" className="admin-form-label">
                        User type
                    </label>
                    <select
                        id="user_type"
                        name="user_type"
                        value={user.user_type ?? user.type ?? ""}
                        onChange={handleChange}
                        className="admin-form-input"
                    >
                        <option value="">Select...</option>
                        {userTypeArr.map((t) => (
                            <option key={t.value} value={t.value}>
                                {t.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <AdminModalFooter
                onCancel={() => setShow("list")}
                onSubmit={handleSubmit}
                cancelText="Cancel"
                submitText="Save"
                submitType="submit"
                submitDisabled={!!emailError || isSubmitting}
            />
        </form>
    );
};

export default AddUserForm;
